import { Link } from 'react-router-dom';

import ActivityBadge from '@/components/ui/activity-badge';
import Sparkline from '@/components/ui/sparkline';
import type { CasinoCard } from '@/types/api';
import { countryName, formatCount, formatDate, formatMoney, formatMoneyFull } from '@/utils/format';

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs text-fg-muted">{label}</p>
    <p className="mt-0.5 font-semibold text-fg-strong">{value}</p>
  </div>
);

/**
 * One casino: brand, owner, activity, 30-day numbers and the daily GGR trend. On a
 * phone the whole card opens the casino (UI-1), and the owner link stays its own target.
 */
const CasinoCardView = ({ casino: c }: { casino: CasinoCard }) => (
  <article className="relative flex flex-col rounded-card bg-surface p-4 shadow-e1">
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-inset text-sm font-semibold text-fg-muted" aria-hidden>
        {(c.brand_name || '?').slice(0, 1).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="truncate font-semibold text-fg-strong">
          <Link
            to={`/casinos/${encodeURIComponent(c.id)}`}
            className="hover:text-brand hover:underline after:absolute after:inset-0 after:rounded-card after:content-[''] md:after:hidden"
          >
            {c.brand_name || 'Unnamed casino'}
          </Link>
        </h2>
        {c.lead_id ? (
          <Link to={`/leads?lead=${c.lead_id}`} className="relative z-10 block truncate py-3 text-sm text-fg-muted hover:text-brand hover:underline md:py-0">
            {c.owner_email}
          </Link>
        ) : (
          <p className="truncate text-sm text-fg-faint">Owner not linked</p>
        )}
      </div>
      <ActivityBadge activity={c.activity} />
    </div>

    {c.metrics ? (
      <div className="mt-4 grid grid-cols-[1fr_1fr_auto] items-end gap-3">
        <Metric label="GGR, 30 days" value={formatMoney(c.metrics.ggr_30d_usd)} />
        <Metric label="Players" value={formatCount(c.metrics.players_30d)} />
        <div className="text-right">
          <p className="text-xs text-fg-muted">
            Velocity <span className="font-semibold text-fg-strong">{c.metrics.velocity.toFixed(1)}</span>
          </p>
          <Sparkline values={c.metrics.trend} format={formatMoneyFull} label={`Daily GGR of ${c.brand_name}`} />
        </div>
      </div>
    ) : (
      <p className="mt-4 text-sm text-fg-faint">{c.status === 'ok' ? 'No metrics available.' : 'Metrics start once the casino is live.'}</p>
    )}

    <p className="mt-auto pt-4 text-xs text-fg-muted">
      {[c.country && countryName(c.country), c.domain, c.launched_at && `live since ${formatDate(c.launched_at)}`].filter(Boolean).join(' · ') || '—'}
    </p>
  </article>
);

export default CasinoCardView;
